import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page, Tab } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useTab,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    useAuth,
} from "@/comn/hooks";
import {
    BASE,
    APIS,
    URLS,
    SF_APRV_STAT_SRCH,
    SG_APRV_RQST_LIST,
    SG_APRV_WAIT_LIST,
    SG_APRV_PRCD_LIST,
    SG_APRV_CMPT_LIST,
} from "./services/PtliAprvService";

/*
 *  Approval Status List
 * !== 결재 현황 목록 ==!
 */
export const PTLI0409004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0019"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [tabIdx, setTabIdx] = useState(0);
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" },
            { area: "comnCd", comnCd: "COM0055" },
            { area: "comnCd", comnCd: "COM0056" },
            { area: "comnCd", comnCd: "COM0043" },
            // !== 신고 문서 코드 추가 필요 (결재 문서 종류 선택용). ==!
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Approval Status Search Form !== 결재 현황 정보 검색 폼 ==!
        aprvStatSrchInfo: useForm({
            defaultSchema: SF_APRV_STAT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
                aprvRqstStrtDt: comnUtils.getDate({ m: -6 }),
                aprvRqstEndDt: comnUtils.getDate(),
            },
        }),
    };

    // Approval Tab List !== 결재대기,결재진행중,결재완료,결재요청 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [
                { label: "L_APRV_WAIT_TAB" },
                { label: "L_APRV_PROG_TAB" },
                { label: "L_APRV_CMPT_TAB" },
                { label: "L_APRV_RQST_TAB" },
            ],
        },
    });

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Waiting for Approval Status List !== 결재 대기 상태 목록 ==!
        aprvWaitList: useGrid({
            defaultSchema: SG_APRV_WAIT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Request Approval Status List !== 결재 요청 상태 목록 ==!
        aprvRqstList: useGrid({
            defaultSchema: SG_APRV_RQST_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Approval in Progress Status List !== 결재 진행중 상태 목록 ==!
        aprvPrcdList: useGrid({
            defaultSchema: SG_APRV_PRCD_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Completed Approval Status List !== 결재 완료 상태 목록 ==!
        aprvCmptList: useGrid({
            defaultSchema: SG_APRV_CMPT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Approval Path Management List !== 결재 대기 상태 목록 조회==!
        getAprvWaitList: useFetch({
            api: (page = grid.aprvWaitList.page) =>
                APIS.getAprvWaitList(form.aprvStatSrchInfo.getValues(), page, grid.aprvWaitList.size),
            enabled:
                tabIdx === 0 && comnUtils.isEmpty(form.aprvStatSrchInfo.errors) && form.aprvStatSrchInfo.isSubmitted,
            key: [grid.aprvWaitList.page, grid.aprvWaitList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.aprvStatSrchInfo.getValues(),
                    page: grid.aprvWaitList.page,
                    size: grid.aprvWaitList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        //Check Approval Path Management List !== 결재 요청 상태 목록 조회==!
        getAprvRqstList: useFetch({
            api: (page = grid.aprvRqstList.page) =>
                APIS.getAprvRqstList(form.aprvStatSrchInfo.getValues(), page, grid.aprvRqstList.size),
            enabled:
                tabIdx === 3 && comnUtils.isEmpty(form.aprvStatSrchInfo.errors) && form.aprvStatSrchInfo.isSubmitted,
            key: [grid.aprvRqstList.page, grid.aprvRqstList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.aprvStatSrchInfo.getValues(),
                    page: grid.aprvRqstList.page,
                    size: grid.aprvRqstList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        //Check Approval Path Management List !== 결재 진행중 상태 목록 조회==!
        getAprvPrcdList: useFetch({
            api: (page = grid.aprvPrcdList.page) =>
                APIS.getAprvPrcdList(form.aprvStatSrchInfo.getValues(), page, grid.aprvPrcdList.size),
            enabled:
                tabIdx === 1 && comnUtils.isEmpty(form.aprvStatSrchInfo.errors) && form.aprvStatSrchInfo.isSubmitted,
            key: [grid.aprvPrcdList.page, grid.aprvPrcdList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.aprvStatSrchInfo.getValues(),
                    page: grid.aprvPrcdList.page,
                    size: grid.aprvPrcdList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        //Check Approval Path Management List !== 결재 완료 상태 목록 조회==!
        getAprvCmptList: useFetch({
            api: (page = grid.aprvCmptList.page) =>
                APIS.getAprvCmptList(form.aprvStatSrchInfo.getValues(), page, grid.aprvCmptList.size),
            enabled:
                tabIdx === 2 && comnUtils.isEmpty(form.aprvStatSrchInfo.errors) && form.aprvStatSrchInfo.isSubmitted,
            key: [grid.aprvCmptList.page, grid.aprvCmptList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.aprvStatSrchInfo.getValues(),
                    page: grid.aprvCmptList.page,
                    size: grid.aprvCmptList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
    };

    // Click on the grid to go to the detail !== 그리드 클릭시 결재 상세 화면으로 이동 ==!
    const render = {
        cell: {
            aprvRqstTtleNm: (props: any) => {
                const { rowValues, value } = props;
                let query = "";
                if (tabIdx == 0) {
                    query = rowValues.itsfAprvYn === "Y" ? "" : "flag=W&"; // aprvWaitList !== 결재대기 ==!
                } else if (tabIdx == 1) {
                    query = "flag=P&"; // aprvProgList !== 결재진행중 ==!
                } else if (tabIdx == 2) {
                    query = "flag=C&"; // aprvCmptList !== 결재완료 ==!
                } else if (tabIdx == 3) {
                    query = "flag=R&"; // aprvRqstList !== 결재요청 ==!
                }
                return (
                    <Link
                        to={`/ptli/comnmdle/aprv/ptli0409005s/${rowValues.aprvId}/${rowValues.usrDmnId}?${query}menuId=menu_ptli0011`}
                    >
                        {value}
                    </Link>
                );
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            form.aprvStatSrchInfo.handleSubmit(
                (data) => {
                    handler.onTabClick(tabIdx);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                },
            )();
        },
        // Click Tab  !== 탭 이동 ==!
        onTabClick: (index: any) => {
            if (index === 0) {
                grid.aprvWaitList.setPage(0);
                fetch.getAprvWaitList.fetch(0);
            }
            if (index === 1) {
                grid.aprvPrcdList.setPage(0);
                fetch.getAprvPrcdList.fetch(0);
            }
            if (index === 2) {
                grid.aprvCmptList.setPage(0);
                fetch.getAprvCmptList.fetch(0);
            }
            if (index === 3) {
                grid.aprvRqstList.setPage(0);
                fetch.getAprvRqstList.fetch(0);
            }
        },
        // Click Apprvoal Request Button !== 결재 요청 버튼 클릭 ==!
        onAprvRqstBtnClick: () => {
            modal.openModal({
                // url: `${constants.APRV_URL}`,
                url: "/comn/comn/ppup/aprvRqstPpup",
                // !== 결재요청으로 넘겨주는 파라미터(업무분류,화면문서코드,결재참고id,결재완료후처리설정id,반려후처리설정id,이전단계반려사용여부) ==!
                params: {
                    bsopClsfCd: "PTL",
                    scrnDocCd: "scrn_ptla0001",
                    aprvReffId: "/" + "devota.materu",
                    aprvAfPrcssCfgnId: "cfgn_ptl0001", // 내부사용자 등록 및 권한 등록
                    rtrnAfPrcssCfgnId: "",
                    cnclAfPrcssCfgnId: "cfgn_ptl0010",
                    rjctAfPrcssCfgnId: "cfgn_ptl0011",
                    preStageRtrnUseYn: "", // 이전단계 반려처리가 필요한 경우에만 'Y'로 넘겨 줄 것 (결재처리 시 이전단계반려 라디오버튼을 보여줌)
                    itsfAprvYn: "", // 업무팀 자체 결재처리 시 'Y'로 넘겨 줄 것 (결재처리탭 비활성화)
                    delYn: "Y", // 결재요청 후 업무 프로세스 완료 후 최종결재처리 할 경우 사용함.
                    docNo: "", // 문서번호
                },
                size: "md",
                draggable: true,
                callback: (data) => {
                    // !== 콜백으로 결재ID를 받는다 ==!
                    console.log("Check Data 결재아이디 >>>>> ", data.aprvId);
                    modal.closeModal();
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        const authList = auth.get("authList");
        if (!comnUtils.isEmpty(authList)) {
            const isAprvChgAuth = authList.some((auth: any) => {
                // Authorization to change approver !== 전자결재 결재자 변경 권한 ==!
                return auth == "auth_ptli0032";
            });
            if (isAprvChgAuth) {
                form.aprvStatSrchInfo.setValue("aprvChgAuthYn", "Y");
            }
        }

        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.aprvStatSrchInfo.schema.bsopClsfCd} />
                            <Group.Control {...form.aprvStatSrchInfo.schema.aprvDocClsf} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.aprvStatSrchInfo.schema.aprvRqstTtleNm} />
                            <Group.Control {...form.aprvStatSrchInfo.schema.docNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.aprvStatSrchInfo.schema.aprvRqstUsrNm} />
                            <Group.Control {...form.aprvStatSrchInfo.schema.aprvPrcssStatCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.aprvStatSrchInfo.schema.frstRgsrDtmRnge} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.aprvStatSrchInfo.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Tab
                        {...tab.tab}
                        onChange={(index) => {
                            setTabIdx(index);
                            handler.onTabClick(index);
                        }}
                    >
                        <Tab.Panel>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid
                                            {...grid.aprvWaitList.grid}
                                            data={fetch.getAprvWaitList.data?.aprvWaitList}
                                            render={render}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Tab.Panel>
                        <Tab.Panel>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid
                                            {...grid.aprvPrcdList.grid}
                                            data={fetch.getAprvPrcdList.data?.aprvProgList}
                                            render={render}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Tab.Panel>
                        <Tab.Panel>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid
                                            {...grid.aprvCmptList.grid}
                                            data={fetch.getAprvCmptList.data?.aprvCmptList}
                                            render={render}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Tab.Panel>
                        <Tab.Panel>
                            <Group>
                                <Group.Body>
                                    <Group.Section>
                                        <Grid
                                            {...grid.aprvRqstList.grid}
                                            data={fetch.getAprvRqstList.data?.aprvRqstList}
                                            render={render}
                                        />
                                    </Group.Section>
                                </Group.Body>
                            </Group>
                        </Tab.Panel>
                    </Tab>
                </Group.Body>
            </Group>
        </Page>
    );
};
